import { useState, type FormEvent } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError } from '../api/client';
import { getMe, login } from '../api/auth';
import { useSession } from '../lib/session';

function safeNext(value: string | null): string {
  if (!value || !value.startsWith('/') || value.startsWith('//') || value.startsWith('/login')) return '/';
  return value;
}

export function LoginPage() {
  const { me, setMe } = useSession();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const next = safeNext(params.get('next'));

  if (me) return <Navigate to={next} replace />;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(email.trim(), password);
      const current = await getMe();
      setMe(current);
      navigate(next, { replace: true });
    } catch (err) {
      setBusy(false);
      if (err instanceof ApiError && (err.status === 401 || err.status === 400)) {
        setError('The email or password is not correct.');
      } else if (err instanceof ApiError && err.status === 429) {
        setError(err.message || 'Too many sign-in attempts. Wait a moment and try again.');
      } else if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Signing in failed. Try again.');
      }
    }
  };

  return (
    <div className="login-page">
      <main className="card login-card">
        <div className="brand">
          <img src="/favicon.svg" alt="" width={28} height={28} />
          PresentationBuilder
        </div>
        <h1 style={{ fontSize: 'var(--text-xl)' }}>Sign in</h1>
        <form className="stack" onSubmit={(event) => void submit(event)}>
          <div className="field">
            <label htmlFor="login-email">Email</label>
            <input
              id="login-email"
              className="input"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
              autoFocus
            />
          </div>
          <div className="field">
            <label htmlFor="login-password">Password</label>
            <input
              id="login-password"
              className="input"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
            />
          </div>
          {error ? (
            <div className="alert alert-error" role="alert">
              {error}
            </div>
          ) : null}
          <button type="submit" className="btn btn-primary" disabled={busy || !email || !password}>
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
      </main>
    </div>
  );
}
